import * as React from 'react'
import moment from 'moment'
import { Link } from 'react-router'
import Select from 'react-select'
import { inject, observer } from 'mobx-react'
import { IInventorySummary, API, ICategoryContract } from '@getgreenline/homi-shared'
import { values, orderBy } from 'lodash'
import { Decimal } from 'decimal.js'
import { Routes } from '../../../../../../constants/Routes'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { Skeleton, Icon, Alert, Checkbox, Tooltip, message, Radio } from 'antd'
import { DebounceInput } from '../../../../../../components/Input'
import {
  parseAndDownloadCSV,
  getCategoryName,
  parseErrorMsg,
  GrPrice,
} from '../../../../../../utilities/helpers'
import { ReportFilter } from '../../ReportFilter'
import { ReportFilterStore } from '../../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../../Track'
import {
  ColumnHeaders,
  DisplayUnit,
  GroupByObject,
  IExportCsvFormat,
  IInventorySummaryWithSubInventories,
  quantityColumnNames,
} from './types'
import { InventoryLogFilterStore } from './InventoryLogFilterStore'
import { getEndOfDay, getStartOfDay } from '../../../../../../utilities/date'
import { IReactionDisposer, reaction } from 'mobx'
import { FeatureToggle } from '../../../../../../constants/FeatureToggles'

interface IProps {
  currentCompanyStore: CurrentCompanyStore
  reportFilterStore: ReportFilterStore
}

interface IState {
  isLoading: boolean
  summaryReports: IInventorySummaryWithSubInventories[]
  groupByCategoryOrProductToggle: GroupByObject
  hideEmptyProductToggle: boolean
  includeDeletedProductsToggle: boolean
  hasFilterChanged: boolean
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class InventorySummaryReport extends React.Component<IProps, IState> {
  inventoryLogFilterStore = new InventoryLogFilterStore(this.props.currentCompanyStore.company!.id)
  disposer: IReactionDisposer | undefined

  constructor(props: IProps) {
    super(props)

    const { query } = this.props.reportFilterStore
    const isValidGroupBy =
      query.groupByCategoryOrProduct === GroupByObject.PRODUCT ||
      query.groupByCategoryOrProduct === GroupByObject.CATEGORY
    this.state = {
      isLoading: true,
      summaryReports: [],
      groupByCategoryOrProductToggle: isValidGroupBy
        ? (query.groupByCategoryOrProduct as GroupByObject)
        : GroupByObject.PRODUCT,
      hideEmptyProductToggle: query.hideEmptyProduct === 'true',
      includeDeletedProductsToggle: query.includeDeletedProducts === 'true',
      hasFilterChanged: false,
    }
  }

  sumBaseValues = {
    openingInventory: 0,
    sold: 0,
    purchased: 0,
    refunded: 0,
    returned: 0,
    lost: 0,
    destroyed: 0,
    transfer: 0,
    audit: 0,
    conversion: 0,
    closingInventory: 0,
    averagePrice: 0,
    averageCost: 0,
  }

  componentDidMount = async () => {
    const { productStore } = this.props.currentCompanyStore

    if (!productStore?.categories) {
      await productStore?.getCategories()
    }

    if (!productStore?.complianceCategories) {
      await productStore?.getComplianceCategories()
    }
    await this.loadReport()
    this.detectChangeInLocationIds()
  }

  componentWillUnmount() {
    this.disposer?.()
  }

  detectChangeInLocationIds = () => {
    this.disposer = reaction(
      () => this.props.reportFilterStore.selectedLocationIds,
      () => this.toggleUpdatedFilters(),
    )
  }

  componentDidUpdate = () => {
    this.toggleUpdatedFilters()
  }

  toggleUpdatedFilters = () => {
    const { startDate, endDate, selectedLocationIds, getFiltersChanged } =
      this.props.reportFilterStore
    const {
      includeDeletedProductsToggle,
      hideEmptyProductToggle,
      groupByCategoryOrProductToggle,
      hasFilterChanged,
    } = this.state
    const filter = {
      locationIds: [...(selectedLocationIds || [])],
      startDate: getStartOfDay(startDate),
      endDate: getEndOfDay(endDate),
      groupByCategoryOrProduct: groupByCategoryOrProductToggle,
      includeDeletedProducts: includeDeletedProductsToggle,
      hideEmptyProduct: hideEmptyProductToggle,
    }
    if (hasFilterChanged === false && getFiltersChanged(filter).length > 0) {
      return this.setState({ hasFilterChanged: true })
    }
    if (hasFilterChanged && getFiltersChanged(filter).length === 0) {
      this.setState({ hasFilterChanged: false })
    }
  }

  loadReport = async () => {
    try {
      const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore
      const {
        groupByCategoryOrProductToggle,
        includeDeletedProductsToggle,
        hideEmptyProductToggle,
      } = this.state
      this.setState({ isLoading: true, summaryReports: [] })

      const filter = {
        locationIds: selectedLocationIds,
        startDate: moment(startDate).startOf('day').toDate(),
        endDate: moment(endDate).endOf('day').toDate(),
        groupByCategoryOrProduct: groupByCategoryOrProductToggle,
        includeDeletedProducts: includeDeletedProductsToggle,
        hideEmptyProduct: hideEmptyProductToggle,
      }
      this.props.reportFilterStore?.saveToQuery(filter)
      const company = this.props.currentCompanyStore.company

      if (company) {
        Track.startTimer('dashboard:reports_inventoryLogSummary')
        const report = await API.getInventorySummaryReport(company.id, filter)

        Track.track('dashboard:reports_inventoryLogSummary', {
          numberOfDays: moment(endDate).diff(startDate, 'days'),
        })

        let summaryReports = report.data
        if (hideEmptyProductToggle) {
          summaryReports = report.data.filter((p) =>
            quantityColumnNames.find((keyName) => p[keyName] !== 0),
          )
        }
        this.setState({ summaryReports })
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occured while loading invertory log summary')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  getConvertedValue(quantity: number, report: IInventorySummary): number {
    const { displayUnit } = this.inventoryLogFilterStore
    switch (displayUnit) {
      case 'price':
        return new Decimal(quantity).times(report.productPrice).toNumber()
      case 'purchasePrice':
        return new Decimal(quantity).times(report.productPurchasePrice).toNumber()
      case 'cannabisWeight':
        return new Decimal(quantity).times(report.productCannabisWeight).toNumber()
      case 'productWeight':
        return new Decimal(quantity).times(report.productWeight).toNumber()
      case 'ml':
        return new Decimal(quantity).times(report.productCannabisVolume).toNumber()
      case 'units':
      default:
        return quantity
    }
  }

  getValueInLocalString(value: number) {
    const { displayUnit } = this.inventoryLogFilterStore
    switch (displayUnit) {
      case 'price':
      case 'purchasePrice':
        return (value / 100).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      case 'cannabisWeight':
      case 'productWeight':
      case 'ml':
      case 'units':
      default:
        return value.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
    }
  }

  addSuffixAndPrefixToDisplay(value: number): string {
    const { displayUnit } = this.inventoryLogFilterStore
    switch (displayUnit) {
      case 'price':
      case 'purchasePrice':
        return `$${this.getValueInLocalString(value)}`
      case 'cannabisWeight':
      case 'productWeight':
      case 'ml':
      default:
        return `${this.getValueInLocalString(value)} g`
      case 'units':
        return this.getValueInLocalString(value)
    }
  }

  toggleViewSettings(toggle: boolean) {
    this.inventoryLogFilterStore.setOpeningInventory(toggle)
    this.inventoryLogFilterStore.setSold(toggle)
    this.inventoryLogFilterStore.setPurchased(toggle)
    this.inventoryLogFilterStore.setRefunded(toggle)
    this.inventoryLogFilterStore.setReturned(toggle)
    this.inventoryLogFilterStore.setLost(toggle)
    this.inventoryLogFilterStore.setDestroyed(toggle)
    this.inventoryLogFilterStore.setTransfer(toggle)
    this.inventoryLogFilterStore.setAudit(toggle)
    this.inventoryLogFilterStore.setConversion(toggle)
    this.inventoryLogFilterStore.setClosingInventory(toggle)
  }

  toggleCategoryId(categoryId: string | null) {
    if (!categoryId) {
      return
    }

    if (this.inventoryLogFilterStore.selectedCategoryIds.includes(categoryId)) {
      // Remove
      this.inventoryLogFilterStore.setSelectedCategoryIds(
        this.inventoryLogFilterStore.selectedCategoryIds.filter((id: string) => id !== categoryId),
      )
    } else {
      // Add
      this.inventoryLogFilterStore.setSelectedCategoryIds([
        ...this.inventoryLogFilterStore.selectedCategoryIds,
        categoryId,
      ])
    }
  }

  export(filteredSummaryReports: IInventorySummaryWithSubInventories[]) {
    const { startDate, endDate } = this.props.reportFilterStore

    const reportJSON: IExportCsvFormat[] = []
    filteredSummaryReports.forEach((summaryReport) => {
      reportJSON.push(
        this.formatJsonToExport({
          report: summaryReport,
          isParentCategory: true,
        }),
      )
      if (summaryReport.subInventories && summaryReport.subInventories.length > 0) {
        summaryReport.subInventories.forEach((subReport) => {
          reportJSON.push(
            this.formatJsonToExport({
              report: subReport,
              isParentCategory: false,
            }),
          )
        })
      }
    })

    parseAndDownloadCSV(`Inventory-summary-report-${startDate}-to-${endDate}`, reportJSON)
  }

  formatJsonToExport({
    report,
    isParentCategory,
  }: {
    report: IInventorySummaryWithSubInventories
    isParentCategory: boolean
  }): IExportCsvFormat {
    const {
      sku,
      barcode,
      name,
      vendor,
      category,
      complianceCategory,
      cannabisWeight,
      productWeight,
      cannabisVolume,
      openingInventory,
      sold,
      purchased,
      refunded,
      returned,
      lost,
      destroyed,
      transfer,
      audit,
      conversion,
      closingInventory,
      averagePrice,
      averageCost,
    } = this.inventoryLogFilterStore
    const groupByCategoryOrProduct = this.props.reportFilterStore?.query.groupByCategoryOrProduct

    const csvObject: IExportCsvFormat = {}
    if (sku && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      csvObject[ColumnHeaders.SKU] = report.productSku || ''
    }
    if (name && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      csvObject[ColumnHeaders.NAME] = report.productName
    }
    if (barcode && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      csvObject[ColumnHeaders.BARCODE] = report.productBarcode || ''
    }
    if (category) {
      const categoryName = report.categoryName || ''
      csvObject[ColumnHeaders.CATEGORY] = isParentCategory ? categoryName : `> ${categoryName}`
    }
    if (vendor && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      csvObject[ColumnHeaders.BRAND] = report.supplierName || ''
    }
    if (complianceCategory) {
      csvObject[ColumnHeaders.COMPLIANCE_CATEGORY] = report.complianceCategoryName || ''
    }
    if (cannabisWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      if (!report.productCannabisWeight) {
        csvObject[ColumnHeaders.CANNABIS_WEIGHT] = ''
      } else {
        csvObject[ColumnHeaders.CANNABIS_WEIGHT] = report.productCannabisWeight + 'g'
      }
    }
    if (productWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      if (!report.productWeight) {
        csvObject[ColumnHeaders.NET_PRODUCT_WEIGHT] = ''
      } else {
        csvObject[ColumnHeaders.NET_PRODUCT_WEIGHT] = report.productWeight + 'g'
      }
    }
    if (cannabisVolume && groupByCategoryOrProduct === GroupByObject.PRODUCT) {
      if (!report.productCannabisVolume) {
        csvObject[ColumnHeaders.CANNABIS_VOLUME] = ''
      } else {
        csvObject[ColumnHeaders.CANNABIS_VOLUME] = report.productCannabisVolume + 'ml'
      }
    }
    if (openingInventory) {
      csvObject[ColumnHeaders.OPENING] = this.getValueInLocalString(report.openingInventory)
    }
    if (sold) {
      csvObject[ColumnHeaders.SOLD] = this.getValueInLocalString(report.sold)
    }
    if (purchased) {
      csvObject[ColumnHeaders.PURCHASED] = this.getValueInLocalString(report.purchased)
    }
    if (refunded) {
      csvObject[ColumnHeaders.REFUNDED] = this.getValueInLocalString(report.refunded)
    }
    if (returned) {
      csvObject[ColumnHeaders.RETURNED] = this.getValueInLocalString(report.returned)
    }
    if (lost) {
      csvObject[ColumnHeaders.LOST] = this.getValueInLocalString(report.lost)
    }
    if (destroyed) {
      csvObject[ColumnHeaders.DESTROYED] = this.getValueInLocalString(report.destroyed)
    }
    if (transfer) {
      csvObject[ColumnHeaders.TRANSFERRED] = this.getValueInLocalString(report.transfer)
    }
    if (audit) {
      csvObject[ColumnHeaders.AUDITED] = this.getValueInLocalString(report.audit)
    }
    if (conversion) {
      csvObject[ColumnHeaders.CONVERTED] = this.getValueInLocalString(report.conversion)
    }
    if (closingInventory) {
      csvObject[ColumnHeaders.CLOSING] = this.getValueInLocalString(report.closingInventory)
    }
    if (groupByCategoryOrProduct === GroupByObject.PRODUCT && averagePrice) {
      csvObject[ColumnHeaders.AVERAGE_PRICE] = GrPrice.convertCentToDollar(report.averagePrice)
    }
    if (groupByCategoryOrProduct === GroupByObject.PRODUCT && averageCost) {
      csvObject[ColumnHeaders.AVERAGE_COST] = GrPrice.convertCentToDollar(report.averageCost)
    }

    return csvObject
  }

  makeInventorySummary(
    hash: { [categoryId: string]: IInventorySummary },
    report: IInventorySummary,
  ): IInventorySummary {
    const foundObject = hash[report.categoryId!]
    return {
      ...foundObject,
      openingInventory: new Decimal(foundObject.openingInventory)
        .plus(report.openingInventory)
        .toNumber(),
      sold: new Decimal(foundObject.sold).plus(report.sold).toNumber(),
      purchased: new Decimal(foundObject.purchased).plus(report.purchased).toNumber(),
      refunded: new Decimal(foundObject.refunded).plus(report.refunded).toNumber(),
      returned: new Decimal(foundObject.returned).plus(report.returned).toNumber(),
      lost: new Decimal(foundObject.lost).plus(report.lost).toNumber(),
      destroyed: new Decimal(foundObject.destroyed).plus(report.destroyed).toNumber(),
      transfer: new Decimal(foundObject.transfer).plus(report.transfer).toNumber(),
      audit: new Decimal(foundObject.audit).plus(report.audit).toNumber(),
      conversion: new Decimal(foundObject.conversion).plus(report.conversion).toNumber(),
      closingInventory: new Decimal(foundObject.closingInventory)
        .plus(report.closingInventory)
        .toNumber(),
    }
  }

  filterParentCategoriesBySelectedId(
    parentCategories: ICategoryContract[],
    selectedCategoryId: string,
  ): ICategoryContract[] {
    // If a category's selected, we only want categories
    // whose id appears as a parent category or child category id
    return parentCategories.filter((parentCategory) => {
      const hasMatchingParentCategoryId = parentCategory.id === selectedCategoryId
      const hasMatchingChildCategoryIds = parentCategory.childCategories.filter((childCategory) => {
        return childCategory.id === selectedCategoryId
      })

      return hasMatchingParentCategoryId || hasMatchingChildCategoryIds.length
    })
  }

  getMatchingSubReportsTotal(allMatchingReports: IInventorySummary[]): any {
    return allMatchingReports.reduce((prev, report) => {
      return {
        openingInventory: prev.openingInventory + report.openingInventory,
        sold: prev.sold + report.sold,
        purchased: prev.purchased + report.purchased,
        refunded: prev.refunded + report.refunded,
        returned: prev.returned + report.returned,
        lost: prev.lost + report.lost,
        destroyed: prev.destroyed + report.destroyed,
        transfer: prev.transfer + report.transfer,
        audit: prev.audit + report.audit,
        conversion: prev.conversion + report.conversion,
        closingInventory: prev.closingInventory + report.closingInventory,
        averagePrice: prev.averagePrice + report.averagePrice,
        averageCost: prev.averageCost + report.averageCost,
      }
    }, this.sumBaseValues)
  }

  getFilteredSummaries(): IInventorySummaryWithSubInventories[] {
    const summaryReports = this.state.summaryReports
    const { productSearch, complianceCategoryId, selectedCategoryId } = this.inventoryLogFilterStore
    const groupByCategoryOrProduct = this.props.reportFilterStore.query.groupByCategoryOrProduct
    let filteredSummaryReports = summaryReports || []

    if (productSearch) {
      filteredSummaryReports = filteredSummaryReports.filter((p) => {
        const skuMatches = (p.productSku || '').toLowerCase().includes(productSearch.toLowerCase())
        const nameMatches = (p.productName || '')
          .toLowerCase()
          .includes(productSearch.toLowerCase())
        return skuMatches || nameMatches
      })
    }

    if (complianceCategoryId) {
      filteredSummaryReports = filteredSummaryReports.filter(
        (p) => p.complianceCategoryId === complianceCategoryId,
      )
    }

    if (selectedCategoryId) {
      // Check if each product has the selected category id as primary category id OR as a parent category id
      filteredSummaryReports = filteredSummaryReports.filter((p) => {
        const isCategoryId = p.categoryId === selectedCategoryId
        const isParentCategoryId =
          p.parentCategoryId !== null && p.parentCategoryId === selectedCategoryId
        return isCategoryId || isParentCategoryId
      })
    }

    filteredSummaryReports = filteredSummaryReports.map((report) => {
      return {
        ...report,
        openingInventory: this.getConvertedValue(report.openingInventory, report),
        sold: this.getConvertedValue(report.sold, report),
        purchased: this.getConvertedValue(report.purchased, report),
        refunded: this.getConvertedValue(report.refunded, report),
        returned: this.getConvertedValue(report.returned, report),
        lost: this.getConvertedValue(report.lost, report),
        destroyed: this.getConvertedValue(report.destroyed, report),
        transfer: this.getConvertedValue(report.transfer, report),
        audit: this.getConvertedValue(report.audit, report),
        conversion: this.getConvertedValue(report.conversion, report),
        closingInventory: this.getConvertedValue(report.closingInventory, report),
      }
    })

    if (groupByCategoryOrProduct === GroupByObject.CATEGORY) {
      const hash: { [categoryId: string]: IInventorySummary } = {}
      const filteredSummaryReportsWithCategoryId = filteredSummaryReports.filter(
        (report) => !!report.categoryId,
      )

      filteredSummaryReportsWithCategoryId.forEach((report) => {
        if (report.categoryId) {
          if (hash[report.categoryId]) {
            hash[report.categoryId] = this.makeInventorySummary(hash, report)
          } else {
            hash[report.categoryId] = report
          }
        }
      })

      // Sort the array of objects by categoryName
      const allSummaryReports = orderBy(values(hash), 'categoryName')
      let parentCategories = this.props.currentCompanyStore.productStore?.categories || []

      if (selectedCategoryId) {
        parentCategories = this.filterParentCategoriesBySelectedId(
          parentCategories,
          selectedCategoryId,
        )
      }

      const orderedParentCategories = orderBy(parentCategories, 'name')

      filteredSummaryReports = orderedParentCategories.map((parentCategory) => {
        const matchingCategoryReports = allSummaryReports.filter(
          (report) => report.categoryId === parentCategory.id,
        )
        const matchingSubReports = allSummaryReports.filter(
          (report) => report.parentCategoryId === parentCategory.id,
        )
        const allMatchingReports = [...matchingCategoryReports, ...matchingSubReports]

        const matchingSubReportsTotals = this.getMatchingSubReportsTotal(allMatchingReports)

        const parentCategoryDetails = {
          categoryId: parentCategory.id,
          categoryName: parentCategory.name,
          complianceCategoryId: parentCategory.complianceCategoryId,
          complianceCategoryName: parentCategory.complianceCategoryName,
          parentCategoryId: parentCategory.parentCategoryId
            ? parentCategory.parentCategoryId
            : null,
        }

        // When no filter's applied, we still want to display categories
        // with no data. dummyProduct is used to create empty category rows
        const dummyProduct = {
          productId: `Dummy-${parentCategory.id}`,
          productSku: null,
          productName: 'DUMMY',
          parentProductId: null,
          parentProductName: null,
          productCannabisVolume: 0,
          productCannabisWeight: 0,
          productPurchasePrice: 0,
          productPrice: 0,
        }

        return {
          ...parentCategoryDetails,
          ...dummyProduct,
          ...matchingSubReportsTotals,
          subInventories: matchingSubReports,
        }
      })
    }
    return filteredSummaryReports
  }

  get isTransferPopoverVisible(): boolean {
    return (
      this.inventoryLogFilterStore.transfer &&
      this.props.reportFilterStore.selectedLocationIds.length > 1
    )
  }

  render() {
    const company = this.props.currentCompanyStore.company
    if (!company) {
      return <Skeleton active />
    }
    const complianceCategories =
      this.props.currentCompanyStore.productStore?.complianceCategories || []
    const categories = this.props.currentCompanyStore.productStore?.flattenedCategories || []

    const { isLoading } = this.state

    const {
      productSearch,
      displayUnit,
      selectedCategoryId,
      complianceCategoryId,
      selectedCategoryIds,

      sku,
      barcode,
      name,
      vendor,
      category,
      complianceCategory,
      cannabisWeight,
      productWeight,
      cannabisVolume,

      openingInventory,
      sold,
      purchased,
      refunded,
      returned,
      lost,
      destroyed,
      transfer,
      audit,
      conversion,
      closingInventory,

      averagePrice,
      averageCost,
    } = this.inventoryLogFilterStore

    const { groupByCategoryOrProduct } = this.props.reportFilterStore.query

    const filteredSummaryReports = this.getFilteredSummaries()
    const totals = filteredSummaryReports.reduce((prev, report) => {
      return {
        openingInventory: prev.openingInventory + report.openingInventory,
        sold: prev.sold + report.sold,
        purchased: prev.purchased + report.purchased,
        refunded: prev.refunded + report.refunded,
        returned: prev.returned + report.returned,
        lost: prev.lost + report.lost,
        destroyed: prev.destroyed + report.destroyed,
        transfer: prev.transfer + report.transfer,
        audit: prev.audit + report.audit,
        conversion: prev.conversion + report.conversion,
        closingInventory: prev.closingInventory + report.closingInventory,
        averagePrice: prev.averagePrice + report.averagePrice,
        averageCost: prev.averageCost + report.averageCost,
      }
    }, this.sumBaseValues)

    const helmet = (
      <Helmet>
        <title>Reports | Inventory Log Summary</title>
        <meta name='page-loaded' content='true' />
      </Helmet>
    )
    return (
      <>
        {helmet}
        <ReportFilter
          maxRangeMonths={12}
          apply={this.loadReport}
          buttonType={this.state.hasFilterChanged ? 'primary' : 'ghost'}
        />

        <div className='rounded background-white p-3 mb-3'>
          <div className='row'>
            <div className='col-md-3'>
              <div>Group by</div>
              <div>
                <Radio
                  checked={this.state.groupByCategoryOrProductToggle === GroupByObject.CATEGORY}
                  onClick={() =>
                    this.setState({
                      groupByCategoryOrProductToggle: GroupByObject.CATEGORY,
                    })
                  }
                >
                  Category
                </Radio>
              </div>
              <div>
                <Radio
                  checked={this.state.groupByCategoryOrProductToggle === GroupByObject.PRODUCT}
                  onClick={() =>
                    this.setState({
                      groupByCategoryOrProductToggle: GroupByObject.PRODUCT,
                    })
                  }
                >
                  Product
                </Radio>
              </div>
              <br />

              <div>Product columns</div>
              <div>
                <Checkbox checked={sku} onClick={() => this.inventoryLogFilterStore.setSku(!sku)}>
                  SKU
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={name}
                  onClick={() => this.inventoryLogFilterStore.setName(!name)}
                >
                  Product name
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={barcode}
                  onClick={() => this.inventoryLogFilterStore.setBarcode(!barcode)}
                >
                  Barcode
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={category}
                  onClick={() => this.inventoryLogFilterStore.setCategory(!category)}
                >
                  Category
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={vendor}
                  onClick={() => this.inventoryLogFilterStore.setVendor(!vendor)}
                >
                  Brand
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={complianceCategory}
                  onClick={() =>
                    this.inventoryLogFilterStore.setComplianceCategory(!complianceCategory)
                  }
                >
                  Compliance category
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={cannabisWeight}
                  onClick={() => this.inventoryLogFilterStore.setCannabisWeight(!cannabisWeight)}
                >
                  Cannabis weight
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={productWeight}
                  onClick={() => this.inventoryLogFilterStore.setProductWeight(!productWeight)}
                >
                  Net product weight
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={cannabisVolume}
                  onClick={() => this.inventoryLogFilterStore.setCannabisVolume(!cannabisVolume)}
                >
                  Cannabis volume
                </Checkbox>
              </div>
            </div>
            <div className='col-md-3'>
              <div>Inventory columns</div>
              <div className='mb-2'>
                <a onClick={() => this.toggleViewSettings(true)}>All</a> |{' '}
                <a onClick={() => this.toggleViewSettings(false)}>None</a>
              </div>
              <div>
                <Checkbox
                  checked={openingInventory}
                  onClick={() =>
                    this.inventoryLogFilterStore.setOpeningInventory(!openingInventory)
                  }
                >
                  Opening inventory
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={sold}
                  onClick={() => this.inventoryLogFilterStore.setSold(!sold)}
                >
                  Sold
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={purchased}
                  onClick={() => this.inventoryLogFilterStore.setPurchased(!purchased)}
                >
                  Purchased
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={refunded}
                  onClick={() => this.inventoryLogFilterStore.setRefunded(!refunded)}
                >
                  Refunded
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={returned}
                  onClick={() => this.inventoryLogFilterStore.setReturned(!returned)}
                >
                  Returned
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={lost}
                  onClick={() => this.inventoryLogFilterStore.setLost(!lost)}
                >
                  Lost
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={destroyed}
                  onClick={() => this.inventoryLogFilterStore.setDestroyed(!destroyed)}
                >
                  Destroyed
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={transfer}
                  onClick={() => this.inventoryLogFilterStore.setTransfer(!transfer)}
                >
                  Transferred
                  {this.isTransferPopoverVisible && (
                    <Tooltip
                      placement='right'
                      title={
                        <>
                          <div className='mb-2'>
                            <b>Multiple locations selected</b>
                          </div>
                          <div className='mb-2'>
                            Transfers between these locations will be cancelled out to be 0.
                          </div>
                          <div>
                            To see transfers from 1 location, please update your location filters.
                          </div>
                        </>
                      }
                    >
                      <Icon style={{ marginLeft: 5 }} type='exclamation-circle' />
                    </Tooltip>
                  )}
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={audit}
                  onClick={() => this.inventoryLogFilterStore.setAudit(!audit)}
                >
                  Audited
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={conversion}
                  onClick={() => this.inventoryLogFilterStore.setConversion(!conversion)}
                >
                  Converted
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={closingInventory}
                  onClick={() =>
                    this.inventoryLogFilterStore.setClosingInventory(!closingInventory)
                  }
                >
                  Closing inventory
                </Checkbox>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='form-group'>
                <div>Product</div>
                <DebounceInput
                  placeholder='Search by name or SKU'
                  value={productSearch}
                  onChange={(e) => this.inventoryLogFilterStore.setProductSearch(e.target.value)}
                />
              </div>
              <div className='form-group'>
                <div>Category</div>
                <Select
                  value={selectedCategoryId}
                  clearable={true}
                  options={categories.map((cc) => {
                    return {
                      label: getCategoryName(cc),
                      value: cc.id,
                    }
                  })}
                  onChange={(option: { label: string; value: string } | undefined) => {
                    if (option) {
                      this.inventoryLogFilterStore.setSelectedCategoryId(option.value)
                    } else {
                      this.inventoryLogFilterStore.setSelectedCategoryId(undefined)
                    }
                  }}
                />
              </div>
              <div className='form-group'>
                <div>Compliance category</div>
                <Select
                  value={complianceCategoryId}
                  clearable={true}
                  options={complianceCategories.map((cc) => {
                    return {
                      label: cc.name,
                      value: cc.id,
                    }
                  })}
                  onChange={(option: { label: string; value: number } | undefined) => {
                    if (option) {
                      this.inventoryLogFilterStore.setComplianceCategoryId(option.value)
                    } else {
                      this.inventoryLogFilterStore.setComplianceCategoryId(undefined)
                    }
                  }}
                />
              </div>
              <div>
                <div>Average</div>
                <div>
                  <Checkbox
                    checked={averagePrice}
                    onClick={() => this.inventoryLogFilterStore.setAveragePrice(!averagePrice)}
                  >
                    Show average price
                  </Checkbox>
                </div>
                <div>
                  <Checkbox
                    checked={averageCost}
                    onClick={() => this.inventoryLogFilterStore.setAverageCost(!averageCost)}
                  >
                    Show average cost
                  </Checkbox>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div>Display units</div>
              <div>
                <Radio
                  checked={displayUnit === 'units'}
                  onClick={() => this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.UNITS)}
                >
                  Units
                </Radio>
              </div>
              <div>
                <Radio
                  checked={displayUnit === 'cannabisWeight'}
                  onClick={() =>
                    this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.CANNABIS_WEIGHT)
                  }
                >
                  Cannabis weight
                </Radio>
              </div>
              <div>
                <Radio
                  checked={displayUnit === 'productWeight'}
                  onClick={() =>
                    this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.PRODUCT_WEIGHT)
                  }
                >
                  Net product weight
                </Radio>
              </div>
              <div>
                <Radio
                  checked={displayUnit === 'ml'}
                  onClick={() => this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.ML)}
                >
                  Cannabis volume
                </Radio>
              </div>
              <div>
                <Radio
                  checked={displayUnit === 'price'}
                  onClick={() => this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.PRICE)}
                >
                  Retail price
                </Radio>
              </div>
              <div>
                <Radio
                  checked={displayUnit === 'purchasePrice'}
                  onClick={() =>
                    this.inventoryLogFilterStore.setDisplayUnit(DisplayUnit.PURCHASE_PRICE)
                  }
                >
                  Wholesale cost
                </Radio>
              </div>
              <br />

              <div>Options</div>
              <div>
                <Checkbox
                  checked={this.state.hideEmptyProductToggle}
                  onClick={() =>
                    this.setState({
                      hideEmptyProductToggle: !this.state.hideEmptyProductToggle,
                    })
                  }
                >
                  Hide empty products
                </Checkbox>
              </div>
              <div>
                <Checkbox
                  checked={this.state.includeDeletedProductsToggle}
                  onClick={() =>
                    this.setState({
                      includeDeletedProductsToggle: !this.state.includeDeletedProductsToggle,
                    })
                  }
                >
                  Include deleted products
                </Checkbox>
              </div>
            </div>
          </div>
        </div>

        {['price', 'purchasePrice'].includes(displayUnit) && (
          <Alert
            showIcon
            className='mb-3'
            type='warning'
            message={displayUnit === 'price' ? 'Showing retail prices' : 'Showing wholesale costs'}
            description={
              <>
                <div>
                  Estimated using{' '}
                  <b>
                    {displayUnit === 'price' ? 'current retail prices' : 'current wholesale costs'}
                  </b>
                  . To get accurate, historical sale and purchase order values, please refer to:
                </div>
                <ul className='mb-0'>
                  <li>
                    <a href={Routes.DASHBOARD_REPORTS(company.id, 'productsOverview')}>
                      Product sales report
                    </a>
                  </li>
                  <li>
                    <a href={Routes.DASHBOARD_REPORTS(company.id, 'categoriesOverview')}>
                      Category sales report
                    </a>
                  </li>
                  <li>
                    <a href={Routes.DASHBOARD_REPORTS(company.id, 'purchaseOrders')}>
                      Purchase order report
                    </a>
                  </li>
                </ul>
              </>
            }
          />
        )}

        <div className='rounded background-white p-3 intercom-mb-1'>
          {isLoading ? (
            <Skeleton active />
          ) : (
            <>
              <button
                className='btn btn-sm btn-light pull-right mb-3'
                onClick={() => this.export(filteredSummaryReports)}
              >
                Export as csv
              </button>
              <div className='table-responsive analyze_table'>
                <table className='table table-sm mb-0'>
                  <thead className='thead-light'>
                    <tr>
                      {sku && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.SKU}</th>
                      )}
                      {name && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.NAME}</th>
                      )}
                      {barcode && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.BARCODE}</th>
                      )}
                      {category && <th>{ColumnHeaders.CATEGORY}</th>}
                      {vendor && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.BRAND}</th>
                      )}
                      {complianceCategory && <th>{ColumnHeaders.COMPLIANCE_CATEGORY}</th>}
                      {cannabisWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.CANNABIS_WEIGHT}</th>
                      )}
                      {productWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.NET_PRODUCT_WEIGHT}</th>
                      )}
                      {cannabisVolume && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th>{ColumnHeaders.CANNABIS_VOLUME}</th>
                      )}
                      {openingInventory && <th className='text-right'>{ColumnHeaders.OPENING}</th>}
                      {sold && (
                        <th className='text-right' style={{ whiteSpace: 'nowrap' }}>
                          {ColumnHeaders.SOLD}{' '}
                          {['price', 'purchasePrice'].includes(displayUnit) && (
                            <Icon type='exclamation-circle' />
                          )}
                        </th>
                      )}
                      {purchased && (
                        <th className='text-right' style={{ whiteSpace: 'nowrap' }}>
                          {ColumnHeaders.PURCHASED}{' '}
                          {['price', 'purchasePrice'].includes(displayUnit) && (
                            <Icon type='exclamation-circle' />
                          )}
                        </th>
                      )}
                      {refunded && <th className='text-right'>{ColumnHeaders.REFUNDED}</th>}
                      {returned && <th className='text-right'>{ColumnHeaders.RETURNED}</th>}
                      {lost && <th className='text-right'>{ColumnHeaders.LOST}</th>}
                      {destroyed && <th className='text-right'>{ColumnHeaders.DESTROYED}</th>}
                      {transfer && <th className='text-right'>{ColumnHeaders.TRANSFERRED}</th>}
                      {audit && <th className='text-right'>{ColumnHeaders.AUDITED}</th>}
                      {conversion && <th className='text-right'>{ColumnHeaders.CONVERTED}</th>}
                      {closingInventory && <th className='text-right'>{ColumnHeaders.CLOSING}</th>}
                      {averagePrice && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th className='text-right'>{ColumnHeaders.AVERAGE_PRICE}</th>
                      )}
                      {averageCost && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <th className='text-right'>{ColumnHeaders.AVERAGE_COST}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSummaryReports.map((product) => {
                      return (
                        <React.Fragment key={product.productId}>
                          <tr>
                            {sku && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td style={{ minWidth: 150, wordBreak: 'break-word' }}>
                                {product.productSku}
                              </td>
                            )}
                            {name && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td style={{ minWidth: 200 }}>
                                <Link
                                  to={Routes.EDIT_PRODUCT(
                                    company.id,
                                    product.parentProductId || product.productId,
                                  )}
                                >
                                  {product.productName}
                                </Link>
                              </td>
                            )}
                            {barcode && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td style={{ minWidth: 150, wordBreak: 'break-all' }}>
                                {product.productBarcode}
                              </td>
                            )}
                            {category &&
                              (groupByCategoryOrProduct === GroupByObject.CATEGORY &&
                              product.subInventories &&
                              product.subInventories.length > 0 ? (
                                <td>
                                  <a onClick={() => this.toggleCategoryId(product.categoryId)}>
                                    {product.categoryName}
                                  </a>
                                </td>
                              ) : (
                                <td>{product.categoryName}</td>
                              ))}
                            {vendor && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td style={{ minWidth: 150, wordBreak: 'break-word' }}>
                                {product.supplierName}
                              </td>
                            )}
                            {complianceCategory && <td>{product.complianceCategoryName}</td>}
                            {cannabisWeight &&
                              groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                                <td>{product.productCannabisWeight || '-'} g</td>
                              )}
                            {productWeight &&
                              groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                                <td>{product.productWeight || '-'} g</td>
                              )}
                            {cannabisVolume &&
                              groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                                <td>{product.productCannabisVolume || '-'} ml</td>
                              )}
                            {openingInventory && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.openingInventory)}
                              </td>
                            )}
                            {sold && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.sold)}
                              </td>
                            )}
                            {purchased && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.purchased)}
                              </td>
                            )}
                            {refunded && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.refunded)}
                              </td>
                            )}
                            {returned && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.returned)}
                              </td>
                            )}
                            {lost && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.lost)}
                              </td>
                            )}
                            {destroyed && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.destroyed)}
                              </td>
                            )}
                            {transfer && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.transfer)}
                              </td>
                            )}
                            {audit && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.audit)}
                              </td>
                            )}
                            {conversion && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.conversion)}
                              </td>
                            )}
                            {closingInventory && (
                              <td className='text-right'>
                                {this.addSuffixAndPrefixToDisplay(product.closingInventory)}
                              </td>
                            )}
                            {averagePrice && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td className='text-right'>
                                {GrPrice.convertCentToDollar(product.averagePrice)}
                              </td>
                            )}
                            {averageCost && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                              <td className='text-right'>
                                {GrPrice.convertCentToDollar(product.averageCost)}
                              </td>
                            )}
                          </tr>
                          {product.subInventories &&
                            groupByCategoryOrProduct === GroupByObject.CATEGORY &&
                            product.categoryId &&
                            selectedCategoryIds.includes(product.categoryId) &&
                            product.subInventories.map((subInventory) => (
                              <tr key={subInventory.productId}>
                                {category && <td className='pl-4'>{subInventory.categoryName}</td>}
                                {complianceCategory && (
                                  <td>{subInventory.complianceCategoryName}</td>
                                )}
                                {openingInventory && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(
                                      subInventory.openingInventory,
                                    )}
                                  </td>
                                )}
                                {sold && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.sold)}
                                  </td>
                                )}
                                {purchased && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.purchased)}
                                  </td>
                                )}
                                {refunded && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.refunded)}
                                  </td>
                                )}
                                {returned && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.returned)}
                                  </td>
                                )}
                                {lost && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.lost)}
                                  </td>
                                )}
                                {destroyed && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.destroyed)}
                                  </td>
                                )}
                                {transfer && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.transfer)}
                                  </td>
                                )}
                                {audit && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.audit)}
                                  </td>
                                )}
                                {conversion && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(subInventory.conversion)}
                                  </td>
                                )}
                                {closingInventory && (
                                  <td className='text-right'>
                                    {this.addSuffixAndPrefixToDisplay(
                                      subInventory.closingInventory,
                                    )}
                                  </td>
                                )}
                              </tr>
                            ))}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                  <tfoot className='tfoot-light'>
                    <tr>
                      {sku && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td className='font-weight-bold'>Total:</td>
                      )}
                      {name && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td style={{ minWidth: 200 }}></td>
                      )}
                      {barcode && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td style={{ minWidth: 150 }}></td>
                      )}
                      {category && (
                        <td className='font-weight-bold'>
                          {groupByCategoryOrProduct === GroupByObject.CATEGORY ? 'Total:' : ''}
                        </td>
                      )}
                      {vendor && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td style={{ minWidth: 150 }}></td>
                      )}
                      {complianceCategory && <td></td>}
                      {cannabisWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td></td>
                      )}
                      {productWeight && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td></td>
                      )}
                      {cannabisVolume && groupByCategoryOrProduct === GroupByObject.PRODUCT && (
                        <td></td>
                      )}
                      {openingInventory && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.openingInventory)}
                        </td>
                      )}
                      {sold && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.sold)}
                        </td>
                      )}
                      {purchased && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.purchased)}
                        </td>
                      )}
                      {refunded && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.refunded)}
                        </td>
                      )}
                      {returned && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.returned)}
                        </td>
                      )}
                      {lost && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.lost)}
                        </td>
                      )}
                      {destroyed && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.destroyed)}
                        </td>
                      )}
                      {transfer && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.transfer)}
                        </td>
                      )}
                      {audit && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.audit)}
                        </td>
                      )}
                      {conversion && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.conversion)}
                        </td>
                      )}
                      {closingInventory && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {this.addSuffixAndPrefixToDisplay(totals.closingInventory)}
                        </td>
                      )}
                      {groupByCategoryOrProduct === GroupByObject.PRODUCT && averagePrice && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {GrPrice.convertCentToDollar(totals.averagePrice)}
                        </td>
                      )}
                      {groupByCategoryOrProduct === GroupByObject.PRODUCT && averageCost && (
                        <td className='text-right font-weight-bold text-nowrap'>
                          {GrPrice.convertCentToDollar(totals.averageCost)}
                        </td>
                      )}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </>
    )
  }
}
